/**
 * SiteLogo: the brand mark of a template's site in a soft tile, or the tool's icon for general templates.
 *
 *   <SiteLogo site={t.site} tool={t.tool} />            40px tile
 *   <SiteLogo site={t.site} tool={t.tool} size="sm" />  28px tile (chips, lists)
 *
 * Logos are bundled locally (lib/icons), so they never load from a third party.
 */
import { Icon } from "@iconify/react";
import type { TemplateSite, TemplateTool } from "@/services/templates.service";

const LOGOS: Record<string, { icon: string; className?: string }> = {
  google: { icon: "flat-color-icons:google" },
  "google-maps": { icon: "logos:google-maps" },
  amazon: { icon: "simple-icons:amazon", className: "text-[#ff9900]" },
  walmart: { icon: "tabler:brand-walmart", className: "text-[#ffc220]" },
  ebay: { icon: "thesvg-color:ebay" },
  indeed: { icon: "simple-icons:indeed", className: "text-[#003a9b] dark:text-[#6b9bff]" },
  linkedin: { icon: "logos:linkedin-icon" },
  youtube: { icon: "logos:youtube-icon" },
  tiktok: { icon: "ic:baseline-tiktok", className: "text-fg" },
  instagram: { icon: "mdi:instagram", className: "text-[#e1306c]" },
  x: { icon: "simple-icons:x", className: "text-fg" },
  facebook: { icon: "logos:facebook" },
  "product-hunt": { icon: "simple-icons:producthunt", className: "text-[#da552f]" },
  zillow: { icon: "simple-icons:zillow", className: "text-[#006aff] dark:text-[#5aa2ff]" },
  reddit: { icon: "logos:reddit-icon" },
  // Marks Iconify lacks live in lib/icons/brands.json, taken from each site's own icon files.
  "google-trends": { icon: "spidra-brand:google-trends" },
  "google-scholar": { icon: "simple-icons:googlescholar", className: "text-[#4285f4] dark:text-[#8ab4f8]" },
  "google-flights": { icon: "spidra-brand:google-flights" },
  polymarket: { icon: "spidra-brand:polymarket", className: "text-[#1652f0] dark:text-[#6f8fff]" },
  kalshi: { icon: "spidra-brand:kalshi", className: "text-[#00b67a] dark:text-[#2fd99a]" },
  coinmarketcap: { icon: "simple-icons:coinmarketcap", className: "text-[#3861fb] dark:text-[#7d97ff]" },
  crunchbase: { icon: "simple-icons:crunchbase", className: "text-[#0288d1] dark:text-[#4fc3f7]" },
};

const TOOL_ICONS: Record<TemplateTool, string> = {
  batch: "lucide:scan-text",
  search: "lucide:search",
  map: "lucide:network",
  crawl: "lucide:waypoints",
  parse: "lucide:file-text",
};

export default function SiteLogo({
  site,
  tool,
  size = "md",
  className = "",
}: {
  site: TemplateSite | null;
  tool: TemplateTool;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const logo = site ? LOGOS[site.key] : null;
  const box = size === "sm" ? "h-7 w-7 rounded-md" : size === "lg" ? "h-12 w-12 rounded-xl" : "h-10 w-10 rounded-lg";
  const glyph = size === "sm" ? "text-base" : size === "lg" ? "text-2xl" : "text-xl";
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center border border-line bg-surface shadow-[0_1px_2px_rgb(0_0_0/0.05)] ${box} ${className}`}
    >
      {logo ? (
        <Icon icon={logo.icon} className={`${glyph} ${logo.className ?? ""}`} />
      ) : (
        <Icon icon={TOOL_ICONS[tool]} className={`${glyph} text-primary-text`} />
      )}
    </span>
  );
}
